/**
 * Window._privacy is typed in packages/layer-base/declarations.d.ts
 */
const integratorIdMap = {
  'lezerij.nl': 'lezerij-nl',
};

/**
 * Brands whose CMP hostname differs from their brand domain.
 *
 * BSVW and BWOL used to be mapped onto their former own domains here. They now resolve
 * to vtwonen.be, which has its own CMP, so they no longer need an override.
 */
const domainMap = {
  'topics.nl': 'topics-nl',
  'topics.be': 'topics-be',
};

/**
 * @see https://myprivacy-static.dpgmedia.net/docs/3-cross-domain.html
 * Set authId cookie on the brand domain and redirect to the original path
 */
export default defineNuxtRouteMiddleware((to) => {
  /**
   * This routeMiddleware should only run in the server context
   * Therefore, early return when it runs in the client context
   */
  if (import.meta.client) return;

  /**
   * This routeMiddleware should only run when Akamai EdgeWorkers are active
   * Apps indicate via `useRuntimeConfig` that they support Akamai EdgeWorkers
   * Therefore, early return when an app indicates it does not support it
   */
  if (!useRuntimeConfig().public.AKAMAI_PRIVACY_GATE) return;

  const host = useRequestHeader('Host');
  const hostname = extractTopLevelDomain(host || '');
  const brandQuery = to.query.brandCode as string | undefined;
  const brandCode = getBrandCode({ brandCode: brandQuery, hostname });

  /**
   * If a brand code isn't found, we don't have enough information to reliably show Privacy Gate
   */
  if (!brandCode) return;

  /**
   * When the query parameter `?pg=false` is supplied, we don't want to show Privacy Gate
   */
  if (to.query.pg === 'false') return;

  // When in Storyblok preview, we don't want to show Privacy Gate
  if (isStoryblokPreview(to)) return;

  /**
   * When we detect the user-agent header to include `TAPP/CI`, we don't want to show Privacy Gate
   *
   * This user-agent modification is present when:
   * - Running in a `Playwright` context (e2e testing)
   *
   * The user-agent modification is made in this file:
   * @file tapp-fe/packages/utilities/src/UserAgent.ts
   */
  if ((useRequestHeader('user-agent') ?? '').includes('TAPP/CI')) return;

  const brandDomain = brandCode ? getDomain(brandCode)?.domain : host;

  loadPrivacyGateScript();

  function loadPrivacyGateScript() {
    // Prevent loading the script multiple times
    if (window?._privacy || !brandDomain) return;

    const key = (brandDomain as keyof typeof domainMap) || brandDomain;
    const keyIntegratorId = (brandDomain as keyof typeof integratorIdMap) || brandDomain;

    const domain = domainMap[key] || brandDomain;
    const integratorId = integratorIdMap[keyIntegratorId] || `https://www.${brandDomain}`;

    useHead({
      link: [
        {
          rel: 'preconnect',
          href: `https://myprivacy-static.dpgmedia.net`,
        },
      ],
      script: [
        {
          key: '_privacy',
          type: 'text/javascript',
          innerHTML: `
            window._privacy = window._privacy || [];
            window.cmpProperties = { baseUrl: '${integratorId}', cmpCname: 'https://cmp.${domain}', language: 'nl' };
          `,
        },
        {
          tagPriority: 'after:script:cmpProperties',
          key: 'privacyGate',
          src: 'https://myprivacy-static.dpgmedia.net/consent.js',
          async: true,
        },
      ],
    });
  }
});
